
import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../../App';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import AuthLoadingScreen from '../AuthLoadingScreen.jsx';
import { ArrowLeft, DollarSign, TrendingUp, Calendar, ArrowUpRight, FileText, PieChart, MessageSquare, Target, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RevenueChart from '../../components/common/RevenueChart.jsx';

const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const FaturamentoScreen = () => {
    const { db, user } = useAppContext();
    const [earnings, setEarnings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    useEffect(() => {
        if (!db || !user) {
            setIsLoading(false);
            return () => {};
        }

        const q = query(
            collection(db, 'earnings'),
            where('serviId', '==', user.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => {
                const item = doc.data();
                // Fallback: Se o timestamp ainda estiver nulo (acabou de ser criado), usa a data atual
                const createdAt = item.createdAt?.toDate ? item.createdAt.toDate() : new Date();
                return { ...item, id: doc.id, dateObject: createdAt };
            });

            // Ordenação por data (usando o objeto Date garantido)
            data.sort((a, b) => b.dateObject.getTime() - a.dateObject.getTime());

            setEarnings(data);
            setIsLoading(false);
        }, (error) => {
            console.error("Erro ao buscar ganhos:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [db, user]);

    // Estatísticas calculadas com proteção contra valores nulos
    const stats = useMemo(() => {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

        const currentDay = now.getDay();
        const diffToMonday = currentDay === 0 ? 6 : currentDay - 1;
        const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMonday).getTime();

        let day = 0, week = 0, month = 0, year = 0;

        earnings.forEach(e => {
            const amt = parseFloat(e.amount) || 0;
            const date = e.dateObject;
            const t = date.getTime();

            if (t >= startOfDay) day += amt;
            if (t >= startOfWeek) week += amt;
            if (date.getMonth() === selectedMonth && date.getFullYear() === selectedYear) month += amt;
            if (date.getFullYear() === selectedYear) year += amt;
        });

        return { day, week, month, year };
    }, [earnings, selectedMonth, selectedYear]);

    const revenueByMonth = useMemo(() => {
        return months.map((_, index) => {
            const total = earnings
                .filter(e => {
                    const d = e.dateObject;
                    return d.getMonth() === index && d.getFullYear() === selectedYear;
                })
                .reduce((acc, e) => acc + (parseFloat(e.amount) || 0), 0);
            return { month: index, total };
        });
    }, [earnings, selectedYear]);

    const filteredList = useMemo(() => {
        return earnings.filter(e => {
            const d = e.dateObject;
            return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
        });
    }, [earnings, selectedMonth, selectedYear]);

    if (isLoading) return <AuthLoadingScreen message="Sincronizando faturamento..." />;

    return (
        <div className="p-4 h-full overflow-y-auto bg-gray-50 pb-24 text-left">
            <header className="flex items-center mb-6 px-1">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200 mr-2 transition-all active:scale-90">
                    <ArrowLeft size={24} className="text-gray-700" />
                </button>
                <h1 className="text-2xl font-bold text-gray-800">Faturamento</h1>
            </header>

            <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white p-4 rounded-3xl shadow-md border border-gray-100">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Hoje</p>
                    <p className="text-lg font-black text-gray-900 leading-none">R$ {stats.day.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-white p-4 rounded-3xl shadow-md border border-gray-100">
                    <div className="flex items-center justify-between mb-1">
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Esta Semana</p>
                        <span className="text-[7px] font-bold text-blue-400 uppercase">Seg-Dom</span>
                    </div>
                    <p className="text-lg font-black text-blue-600 leading-none">R$ {stats.week.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-[2.5rem] shadow-xl relative overflow-hidden mb-6">
                <div className="relative z-10">
                    <div className="flex items-center text-blue-400 mb-2">
                        <Target size={16} className="mr-2" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">Receita: {months[selectedMonth]}</span>
                    </div>
                    <p className="text-4xl font-black text-white tracking-tighter">
                        <span className="text-xl mr-1 text-slate-500 font-bold">R$</span>
                        {stats.month.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mt-4 pt-4 border-t border-slate-800">Total Anual: R$ {stats.year.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                </div>
                <TrendingUp size={120} className="absolute -right-4 -bottom-4 opacity-10 text-blue-500" />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
                <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="p-3 bg-white border-2 border-gray-100 rounded-2xl font-bold text-gray-700 text-xs outline-none">
                    {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="p-3 bg-white border-2 border-gray-100 rounded-2xl font-bold text-gray-700 text-xs outline-none">
                    {[2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
            </div>

            <div className="bg-white p-6 rounded-[2.5rem] shadow-lg border border-gray-50 mb-8 min-h-[320px]">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6 flex items-center">
                    <BarChart3 size={14} className="mr-2" /> Performance Mensal
                </h3>
                <div className="h-64 w-full">
                    {earnings.length > 0 ? (
                        <RevenueChart data={revenueByMonth} activeMonth={selectedMonth} />
                    ) : (
                        <div className="h-full flex items-center justify-center text-gray-300 italic text-sm font-medium">Aguardando dados...</div>
                    )}
                </div>
            </div>

            <div className="pb-10">
                <div className="flex items-center justify-between mb-4 px-2">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Extrato de Recebimentos</h3>
                    <span className="text-[9px] font-black bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full">{filteredList.length} ITENS</span>
                </div>

                <div className="space-y-3">
                    {filteredList.length > 0 ? filteredList.map(item => (
                        <div key={item.id} onClick={() => item.chatId && navigate(`/servi/chat/${item.chatId}`)} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex justify-between items-center active:scale-[0.98] transition-all cursor-pointer">
                            <div className="flex items-center min-w-0">
                                <div className="bg-emerald-50 p-3 rounded-xl mr-4"><ArrowUpRight className="text-emerald-600 w-5 h-5" /></div>
                                <div className="min-w-0">
                                    <p className="font-bold text-gray-800 text-sm truncate pr-2">{item.description || 'Serviço'}</p>
                                    <p className="text-[9px] text-gray-400 font-bold uppercase">{item.dateObject.toLocaleDateString('pt-BR')}</p>
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <p className="font-black text-emerald-600 text-sm">+ R$ {parseFloat(item.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                                <MessageSquare size={12} className="text-gray-300 mt-1 inline" />
                            </div>
                        </div>
                    )) : (
                        <div className="text-center py-12 bg-white rounded-3xl border-2 border-dashed border-gray-100">
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Nenhum ganho neste período</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FaturamentoScreen;
